"use client";

declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: any;
    OneSignalInitialized?: boolean;
    OneSignalInitCallback?: () => void;
  }
}

export interface OneSignalConfig {
  appId: string;
  safari_web_id?: string;
  notifyButton?: {
    enable: boolean;
  };
  allowLocalhostAsSecureOrigin?: boolean;
}

export const initializeOneSignal = async (appId: string): Promise<void> => {
  if (typeof window === "undefined") {
    console.warn("⚠️ initializeOneSignal called on server side");
    return;
  }

  console.log("🔧 initializeOneSignal: Starting with App ID:", appId?.substring(0, 8) + "...");

  return new Promise((resolve, reject) => {
    const initOneSignal = () => {
      try {
        console.log("🔧 initializeOneSignal: Checking window.OneSignal:", {
          exists: !!window.OneSignal,
          hasInit: !!(window.OneSignal && window.OneSignal.init),
          hasSDK: !!(window.OneSignal && window.OneSignal.SDK)
        });

        if (window.OneSignal && window.OneSignal.init) {
          console.log("🔧 initializeOneSignal: Calling window.OneSignal.init...");
          window.OneSignal.init({
            appId: appId,
            safari_web_id: process.env.NEXT_PUBLIC_ONESIGNAL_SAFARI_WEB_ID,
            notifyButton: {
              enable: false,
            },
            allowLocalhostAsSecureOrigin: process.env.NODE_ENV === "development",
            serviceWorkerParam: { scope: "/" },
            serviceWorkerPath: "OneSignalSDKWorker.js",
          });

          console.log("✅ OneSignal initialized successfully");
          resolve();
          return true;
        }
        return false;
      } catch (error) {
        console.error("❌ Error in initOneSignal:", error);
        reject(error);
        return false;
      }
    };

    if (initOneSignal()) {
      return;
    }

    console.log("🔧 initializeOneSignal: Waiting for SDK to be ready...");
    let checkCount = 0;
    const checkOneSignal = setInterval(() => {
      checkCount++;
      if (window.OneSignal && window.OneSignal.init) {
        console.log(`✅ OneSignal SDK ready after ${checkCount * 100}ms`);
        clearInterval(checkOneSignal);
        if (!initOneSignal()) {
          reject(new Error("Failed to initialize OneSignal"));
        }
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkOneSignal);
      if (!window.OneSignal || !window.OneSignal.init) {
        console.error("❌ OneSignal SDK failed to load after 15 seconds");
        console.error("💡 Debug info:", {
          windowOneSignal: !!window.OneSignal,
          hasInit: !!(window.OneSignal && window.OneSignal.init),
          userAgent: navigator.userAgent.substring(0, 50)
        });
        reject(new Error("OneSignal SDK failed to load"));
      }
    }, 15000);
  });
};

export const getOneSignalUserId = async (): Promise<string | null> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return null;
  }

  try {
    if (window.OneSignal.User && window.OneSignal.User.PushSubscription) {
      const userId = window.OneSignal.User.PushSubscription.id;
      return userId || null;
    }
    
    if (window.OneSignal.SDK) {
      const user = await window.OneSignal.SDK.User;
      if (user && user.PushSubscription) {
        const subscriptionId = await user.PushSubscription.id;
        return subscriptionId || null;
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error getting OneSignal user ID:", error);
    return null;
  }
};

export const getOneSignalSubscriptionToken = async (): Promise<string | null> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return null;
  }

  try {
    if (window.OneSignal.User && window.OneSignal.User.PushSubscription) {
      const token = window.OneSignal.User.PushSubscription.token;
      return token || null;
    }
    
    if (window.OneSignal.SDK) {
      const user = await window.OneSignal.SDK.User;
      if (user && user.PushSubscription) {
        const token = await user.PushSubscription.token;
        return token || null;
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error getting OneSignal subscription token:", error);
    return null;
  }
};


export const isOneSignalSubscribed = async (): Promise<boolean> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return false;
  }

  try {
    if (window.OneSignal.User && window.OneSignal.User.PushSubscription) {
      const isSubscribed = window.OneSignal.User.PushSubscription.optedIn;
      return isSubscribed || false;
    }
    
    if (window.OneSignal.SDK) {
      const user = await window.OneSignal.SDK.User;
      if (user && user.PushSubscription) {
        const isSubscribed = await user.PushSubscription.optedIn;
        return isSubscribed || false;
      }
    }
    
    return false;
  } catch (error) {
    console.error("Error checking OneSignal subscription:", error);
    return false;
  }
};

export const requestOneSignalPermission = async (): Promise<boolean> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return false;
  }

  try {
    if (window.OneSignal.Notifications && window.OneSignal.Notifications.requestPermission) {
      await window.OneSignal.Notifications.requestPermission();
      const isSubscribed = await isOneSignalSubscribed();
      return isSubscribed;
    }
    
    if (window.OneSignal.SDK) {
      const notifications = await window.OneSignal.SDK.Notifications;
      if (notifications) {
        await notifications.requestPermission();
        const isSubscribed = await isOneSignalSubscribed();
        return isSubscribed;
      }
    }
    
    return false;
  } catch (error) {
    console.error("Error requesting OneSignal permission:", error);
    return false;
  }
};

export const registerOneSignal = async (): Promise<string | null> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return null;
  }

  try {
    const permission = await requestOneSignalPermission();
    if (!permission) {
      console.warn("Push notification permission denied");
      return null;
    }

    const userId = await getOneSignalUserId();
    return userId;
  } catch (error) {
    console.error("Error registering OneSignal:", error);
    return null;
  }
};

export const setupOneSignalNotificationHandler = async (
  onNotificationReceived: (notification: any) => void
): Promise<void> => {
  if (typeof window === "undefined" || !window.OneSignal) {
    return;
  }

  try {
    if (window.OneSignal.Notifications) {
      window.OneSignal.Notifications.addEventListener("click", (event: any) => {
        console.log("OneSignal notification clicked:", event);
        onNotificationReceived(event);
      });

      window.OneSignal.Notifications.addEventListener("foregroundWillDisplay", (event: any) => {
        console.log("OneSignal notification received in foreground:", event);
        onNotificationReceived(event);
      });
    }
    
    if (window.OneSignal.SDK) {
      const notifications = await window.OneSignal.SDK.Notifications;
      if (notifications) {
        notifications.addEventListener("click", (event: any) => {
          console.log("OneSignal notification clicked:", event);
          onNotificationReceived(event);
        });

        notifications.addEventListener("foregroundWillDisplay", (event: any) => {
          console.log("OneSignal notification received in foreground:", event);
          onNotificationReceived(event);
        });
      }
    }
  } catch (error) {
    console.error("Error setting up OneSignal notification handler:", error);
  }
};
